import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFetDashboard } from '../api/exams';
import { getLandingConfig } from '../api/auth';

// FET Preparation Dashboard — full implementation matching the client's
// reference design. Nine sections, all wired to /api/fet/dashboard/ which
// computes everything from real attempt + response rows (no fake data).
//
// Layout (desktop ≥ 1100px):
//   ┌ Header (title + bell + avatar) ─────────────────────────────────┐
//   ├ 5 Top stat cards (Completed / Score / Level / Streak / Week) ──┤
//   ├ 3-col main row: [Continue] [Recommended] [Donut]   ─────────────┤
//   ├ 2-col row: [All Exams table]      [Skills + Achievements]     ─┤
//   ├ Bottom: [Weekly streak]  [Focus]  [Plan]  [Motivation]        ─┤
//   └ © 2026 FET Prep ────────────────────────────────────────────────┘

const C = {
  ink: '#0F172A',
  body: '#475569',
  muted: '#64748B',
  faint: '#94A3B8',
  border: '#E2E8F0',
  surfaceSoft: '#F1F5F9',
  bg: '#F8FAFC',
  white: '#FFFFFF',
  indigo: '#5B52F0',
  indigoSoft: '#EEF2FF',
  green: '#16A34A',
  greenLight: '#22C55E',
  greenSoft: '#ECFDF5',
  greenInk: '#15803D',
  amber: '#F59E0B',
  amberSoft: '#FEF3C7',
  amberInk: '#92400E',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redInk: '#7F1D1D',
  rose: '#F43F5E',
  roseSoft: '#FFE4E6',
  pink: '#EC4899',
  blue: '#3B82F6',
  blueSoft: '#EFF6FF',
  blueInk: '#1D4ED8',
};

const SKILL_META = {
  writing: { label: 'Writing', icon: '✎', tone: C.indigo, bg: C.indigoSoft },
  reading: { label: 'Reading', icon: '📖', tone: C.blue, bg: C.blueSoft },
  listening: { label: 'Listening', icon: '🎧', tone: C.amber, bg: C.amberSoft },
  speaking: { label: 'Speaking', icon: '🎙', tone: C.rose, bg: C.roseSoft },
};

function buildSkillHref(examId, skill) {
  if (!examId) return '/';
  // Reading entry runs the user through the 5-step informational walkthrough
  // (parts overview, practice vs exam, level + Arabic toggle) before
  // dropping into reading-home. That's the version the user wants — the
  // walkthrough is a "what to expect" screen, not a pre-exam confirmation.
  if (skill === 'reading') return `/?mode=reading-onboarding&examId=${examId}`;
  return `/exam/${examId}?mode=practice${skill ? `&section=${skill}` : ''}`;
}

const fmtDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// Cache key for stale-while-revalidate. Keyed by user id so two students
// on the same browser don't see each other's stats. TTL controls how long
// stale data is acceptable to render before forcing a fresh fetch.
const DASHBOARD_CACHE_KEY = (uid) => `fet_dashboard_cache_${uid || 'anon'}`;
const DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;  // 5 minutes

export default function FETDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  // Pre-seed `data` from localStorage on first render so the dashboard
  // appears instantly for return visitors. We still fetch fresh data and
  // swap it in once it lands (stale-while-revalidate).
  const [data, setData] = useState(() => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = window.localStorage.getItem(DASHBOARD_CACHE_KEY('seed'));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.data || null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);  // background refresh indicator
  const [error, setError] = useState('');
  const [sectionsEnabled, setSectionsEnabled] = useState({ writing: true, reading: true, speaking: true });
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  // Server-paginated exam list state. The dashboard endpoint slices
  // `all_exams` to this page server-side (5 rows by default) while
  // still computing aggregate stats from the full set — so the skill
  // cards and overall % stay accurate even though the wire payload
  // shrinks dramatically.
  const EXAMS_PAGE_SIZE = 5;
  const [examPage, setExamPage] = useState(1);
  const [examPageLoading, setExamPageLoading] = useState(false);
  const [examSkill, setExamSkill] = useState('all');
  // Cache of slim dashboard responses keyed by `${skill}:${page}`.
  // Tab switches and pagination clicks look here first before hitting
  // the network — round-trips to Heroku run ~1s each from many
  // regions, so a cached page swap feels instant whereas a network
  // round-trip feels laggy. We prefetch all three skill tabs after
  // the initial load so the first time a student clicks "Reading"
  // there's no waiting at all.
  const examPageCacheRef = useRef(new Map());

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login?next=/dashboard', { replace: true }); return; }
    let cancelled = false;

    // Try to render the cached payload first so the page is instant.
    let cachedRendered = false;
    try {
      const raw = window.localStorage.getItem(DASHBOARD_CACHE_KEY(user.id));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data && parsed.savedAt && (Date.now() - parsed.savedAt) < DASHBOARD_CACHE_TTL_MS * 4) {
          // Up to 20 minutes old — still useful to render while we refetch.
          setData(parsed.data);
          cachedRendered = true;
          setLoading(false);
          setRefreshing(true);
        }
      }
    } catch { /* ignore */ }

    if (!cachedRendered) setLoading(true);
    setError('');
    // Fetch the dashboard first and unblock the UI as soon as it
    // resolves. The landing config (just admin section toggles) is
    // a lower-priority secondary call and is fired in parallel but
    // doesn't gate the first paint — previously we awaited both via
    // Promise.all and a slow landing-config endpoint would hold up the
    // dashboard rendering even though we already had its data.
    getFetDashboard({ page: 1, pageSize: EXAMS_PAGE_SIZE, skill: 'all' })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setExamPage(1);
        setExamSkill('all');
        try {
          const blob = JSON.stringify({ data: d, savedAt: Date.now() });
          window.localStorage.setItem(DASHBOARD_CACHE_KEY(user.id), blob);
          window.localStorage.setItem(DASHBOARD_CACHE_KEY('seed'), blob);
        } catch { /* quota / private mode */ }
      })
      .catch((e) => {
        if (cancelled || cachedRendered) return;
        const status = e?.response?.status;
        const body = e?.response?.data;
        const msg = body?.detail || body?.error || body?.message
          || (typeof body === 'string' ? body : null)
          || e?.message
          || 'Could not load your dashboard.';
        // eslint-disable-next-line no-console
        console.error('Dashboard load failed', { status, response: body, error: e });
        setError(status ? `${status} — ${msg}` : msg);
      })
      .finally(() => { if (!cancelled) { setLoading(false); setRefreshing(false); } });

    // Background fetch — section toggles only matter for hiding/showing
    // skill cards. Defaults are sensible, so we let this race the
    // dashboard render rather than blocking it.
    getLandingConfig()
      .then((cfg) => {
        if (cancelled || !cfg?.sections) return;
        setSectionsEnabled({
          writing: cfg.sections.writing !== false,
          reading: cfg.sections.reading !== false,
          speaking: cfg.sections.speaking !== false,
        });
      })
      .catch(() => { /* defaults are fine */ });

    return () => { cancelled = true; };
  }, [authLoading, user, navigate]);

  // Refetch a different page of `all_exams` on demand. Only the
  // `all_exams` slice + pagination metadata change; the rest of the
  // dashboard payload is replaced wholesale (server-cached so this is
  // a fast second hit). We intentionally don't cache the previous page
  // payload here — the aggregates can drift between hits if attempts
  // are completing in the background, so the fresher data wins.
  const loadExamPage = useCallback(async (page, skill) => {
    if (!user) return;
    const nextSkill = skill === undefined ? examSkill : skill;
    const cacheKey = `${nextSkill}:${page}`;

    // Cache hit — apply instantly. No spinner, no flicker, no
    // network. The "stale" risk is negligible: the only thing that
    // changes between page clicks is the student's own progress on
    // exams they've just opened, which they're rarely doing
    // mid-pagination.
    const cached = examPageCacheRef.current.get(cacheKey);
    if (cached) {
      setData((prev) => ({
        ...(prev || {}),
        all_exams: cached.all_exams || [],
        all_exams_pagination: cached.all_exams_pagination || null,
      }));
      setExamPage(page);
      setExamSkill(nextSkill);
      return;
    }

    setExamPageLoading(true);
    try {
      const slim = await getFetDashboard({ page, pageSize: EXAMS_PAGE_SIZE, skill: nextSkill, slim: true });
      examPageCacheRef.current.set(cacheKey, slim);
      setData((prev) => ({
        ...(prev || {}),
        all_exams: slim.all_exams || [],
        all_exams_pagination: slim.all_exams_pagination || null,
      }));
      setExamPage(page);
      setExamSkill(nextSkill);
    } catch (err) {
      console.error('Page load failed', err);
    } finally {
      setExamPageLoading(false);
    }
  }, [user, examSkill]);

  // Tab change handler — resets to page 1 with the new skill so the
  // user never lands on an out-of-range page when switching from a
  // tab with 20 exams (page 4) to one with 5 (only page 1 exists).
  const onSkillTabChange = useCallback((skill) => {
    loadExamPage(1, skill);
  }, [loadExamPage]);

  // After the initial dashboard load lands, prefetch page 1 of each
  // skill tab in the background and cache it. The first time the
  // student clicks Writing/Reading/Speaking, there's no network
  // round-trip — the table swaps instantly from cache. Without this
  // every tab click cost a full ~1s round-trip to Heroku, which felt
  // like the page was hanging.
  useEffect(() => {
    if (!data || loading) return;
    if (!data.skills_aggregate) return; // initial load incomplete
    const skillsToPrefetch = ['all', 'writing', 'reading', 'speaking'];
    skillsToPrefetch.forEach((skill) => {
      const key = `${skill}:1`;
      if (examPageCacheRef.current.has(key)) return;
      // The aggregate tells us how many exams each tab has — skip
      // tabs with zero so we don't fire a useless request.
      if (skill !== 'all' && (data.skills_aggregate?.[skill]?.total || 0) === 0) return;
      getFetDashboard({ page: 1, pageSize: EXAMS_PAGE_SIZE, skill, slim: true })
        .then((slim) => { examPageCacheRef.current.set(key, slim); })
        .catch(() => { /* prefetch failures are silent — the actual click will retry */ });
    });
  }, [data, loading]);

  const isMobile = width < 760;
  const isCompact = width < 1100;

  // Read writing drafts from localStorage so an exam with unsaved-but-typed
  // text still shows as "in progress" on the dashboard. Without this, the
  // user types Q1, leaves, returns and sees the exam as "not started" —
  // because the backend never received any submitted response.
  // Key shape: fet_writing_draft_<userId>_<examId> → { answers, savedAt }
  const draftsByExamId = useMemo(() => {
    const out = {};
    if (!user?.id) return out;
    const prefix = `fet_writing_draft_${user.id}_`;
    try {
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const examId = key.slice(prefix.length);
        try {
          const parsed = JSON.parse(window.localStorage.getItem(key) || '{}');
          const answers = parsed?.answers || {};
          const draftedCount = Object.values(answers).filter((v) => (v || '').trim().length > 0).length;
          if (draftedCount > 0) {
            out[examId] = { draftedCount, savedAt: parsed.savedAt || null };
          }
        } catch { /* ignore corrupt entries */ }
      }
    } catch { /* private mode */ }
    return out;
  }, [user?.id, data]);  // re-scan when fresh dashboard data loads

  // Read reading exam drafts from localStorage. Same idea as writing — a
  // student can start the reading exam, answer some questions, then close
  // the tab. Without this, the dashboard shows reading as "not started" or
  // "0/6 parts" even though they have answers saved locally.
  // Key shape: fet_reading_exam_<userId>_<examId> → {
  //   answersByPart, startedAt, savedAt, totalQuestions, answeredCount
  // }
  const readingDraftsByExamId = useMemo(() => {
    const out = {};
    if (!user?.id) return out;
    const prefix = `fet_reading_exam_${user.id}_`;
    try {
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const examId = key.slice(prefix.length);
        try {
          const parsed = JSON.parse(window.localStorage.getItem(key) || '{}');
          const answeredCount = Number(parsed?.answeredCount) || 0;
          const totalQuestions = Number(parsed?.totalQuestions) || 0;
          if (answeredCount > 0 && totalQuestions > 0) {
            out[examId] = {
              answeredCount,
              totalQuestions,
              savedAt: parsed.savedAt || parsed.startedAt || null,
            };
          }
        } catch { /* ignore corrupt entries */ }
      }
    } catch { /* private mode */ }
    return out;
  }, [user?.id, data]);

  // Apply admin section toggles to the per-exam summaries. The backend can't
  // know about admin section toggles (e.g. Speaking = "Coming soon"), so we
  // strip disabled skills here AND recompute every aggregate (tasks_done,
  // tasks_total, pct, status, next_skill) from the filtered skill set.
  // Without recomputing `status` the All-tab and per-skill tabs disagree on
  // whether an exam is "completed" — that's the inconsistency.
  const filteredItem = useMemo(() => (item) => {
    if (!item) return null;
    const order = ['writing', 'reading', 'speaking'];
    const skills = {};
    let totalDone = 0, totalMax = 0, nextSkill = null;
    order.forEach((sk) => {
      const s = item.skills?.[sk];
      if (!s) return;
      const enabled = sectionsEnabled[sk] !== false;
      const has = s.has_content && enabled;
      // Defensive clamp: writing always has 2 tasks max (Part 1 + Part 2).
      // Older backend deploys returned a per-question total (e.g. 3 when
      // Part 2 has 3 topic options) — that gives 1/3 = 33% which is wrong.
      // Cap to 2 here so the UI shows correct fractions regardless of which
      // backend version is running.
      let normalised = { ...s, has_content: has };
      if (sk === 'writing' && (s.total || 0) > 2) {
        const cappedDone = Math.min(s.done || 0, 2);
        normalised = {
          ...normalised,
          total: 2,
          done: cappedDone,
          pct: cappedDone === 0 ? 0 : Math.round((cappedDone / 2) * 100),
          status: cappedDone >= 2 ? 'completed' : cappedDone > 0 ? 'in_progress' : 'not_started',
        };
      }
      skills[sk] = normalised;
      if (!has) return;
      totalDone += normalised.done || 0;
      totalMax += normalised.total || 0;
      if (!nextSkill && normalised.status !== 'completed') nextSkill = sk;
    });

    // Merge in any local writing draft for this exam — so an exam where
    // the user typed Q1 but never submitted still surfaces on the dashboard.
    // The draft never adds completed tasks (those still come from the
    // backend), but it can flip status from not_started → in_progress and
    // pick writing as the next-skill if writing was previously untouched.
    //
    // Important: a draft is only "live" when writing isn't already done.
    // Once writing is completed, any leftover draft is stale (the user
    // already submitted) — drop it so we don't:
    //   • show a misleading "💾 Draft" chip on a completed exam, or
    //   • override next_skill back to writing (sending Continue to the
    //     wrong section).
    const writingSkill = skills.writing;
    const writingCompleted = writingSkill && writingSkill.status === 'completed';
    const draft = draftsByExamId[item.exam_id];
    let hasDraft = false;
    let draftSavedAt = null;
    if (draft && writingSkill && writingSkill.has_content && !writingCompleted) {
      hasDraft = true;
      draftSavedAt = draft.savedAt;
      skills.writing = { ...writingSkill, has_draft: true, draft_count: draft.draftedCount, draft_saved_at: draft.savedAt };
      if (skills.writing.status === 'not_started') {
        skills.writing = { ...skills.writing, status: 'in_progress' };
      }
      // Only force-override next_skill back to writing if writing isn't done.
      if (skills.writing.status !== 'completed' && (!nextSkill || nextSkill !== 'writing')) nextSkill = 'writing';
    } else if (draft && writingCompleted) {
      // Stale draft — submit must have happened on a different device or
      // the cleanup didn't fire. Clear it so we don't keep nagging.
      try { window.localStorage.removeItem(`fet_writing_draft_${user?.id || 'anon'}_${item.exam_id}`); } catch {}
    }

    // Same idea for reading exam drafts. The backend reports reading at the
    // PART level (parts submitted / parts available). A student who answered
    // 12 of 30 questions but hasn't submitted any part shows up as
    // "0/6 parts → not started" — that's wrong. Override the reading skill's
    // done/total/pct to use the saved questions count so the row's progress
    // bar reflects real progress and the exam surfaces in Continue Your Exam.
    const readingSkill = skills.reading;
    const readingCompleted = readingSkill && readingSkill.status === 'completed';
    const rDraft = readingDraftsByExamId[item.exam_id];
    if (rDraft && readingSkill && readingSkill.has_content && !readingCompleted) {
      const answered = Math.min(rDraft.answeredCount, rDraft.totalQuestions);
      const totalQ = rDraft.totalQuestions || 0;
      const skillPct = totalQ ? Math.round((answered / totalQ) * 100) : 0;
      const wasNotStarted = readingSkill.status === 'not_started';
      // Adjust the skill-level numbers — but keep the part-level totals on
      // the exam-row counters in sync too, by re-deriving the deltas.
      totalDone -= readingSkill.done || 0;
      totalMax -= readingSkill.total || 0;
      const newReading = {
        ...readingSkill,
        done: answered,
        total: totalQ,
        pct: skillPct,
        status: answered > 0 ? 'in_progress' : readingSkill.status,
        has_draft: true,
        draft_count: answered,
        draft_saved_at: rDraft.savedAt,
      };
      skills.reading = newReading;
      totalDone += newReading.done;
      totalMax += newReading.total;
      hasDraft = true;
      if (!draftSavedAt || (rDraft.savedAt && rDraft.savedAt > draftSavedAt)) {
        draftSavedAt = rDraft.savedAt;
      }
      if (wasNotStarted && (!nextSkill || nextSkill === 'speaking')) nextSkill = 'reading';
    } else if (rDraft && readingCompleted) {
      // Stale reading draft — clear it.
      try { window.localStorage.removeItem(`fet_reading_exam_${user?.id || 'anon'}_${item.exam_id}`); } catch {}
    }

    // Recompute overall status from filtered totals — must match what each
    // skill tab shows. Drafts can promote not_started → in_progress.
    let status;
    if (totalMax === 0) status = 'not_started';
    else if (totalDone >= totalMax) status = 'completed';
    else if (totalDone > 0) status = 'in_progress';
    else status = 'not_started';
    if (hasDraft && status === 'not_started') status = 'in_progress';

    return {
      ...item,
      skills,
      next_skill: nextSkill,
      tasks_done: totalDone,
      tasks_total: totalMax,
      pct: totalMax ? Math.round((totalDone / totalMax) * 100) : 0,
      status,
      has_draft: hasDraft,
      draft_saved_at: draftSavedAt,
    };
  }, [sectionsEnabled, draftsByExamId, readingDraftsByExamId, user?.id]);

  // Pre-flight confirmation only for skills where the click leads STRAIGHT
  // into a real exam attempt:
  //   • Writing  — Start opens the timed writing flow.
  //   • Speaking — same idea (when it ships).
  // Reading is exempt because the reading flow first shows an informational
  // walkthrough then a separate practice screen; the confirmation lives on
  // ReadingHome, fired only when the student picks Exam mode.
  const [examConfirm, setExamConfirm] = useState(null); // { item, skill, action }
  // Stable handlers so memoized children (like the 50+ ExamRow instances
  // rendered by AllExamsTable) don't re-render every time this dashboard
  // re-renders for an unrelated state change. Without useCallback, every
  // render hands rows a fresh function reference, defeating React.memo.
  const openExam = useCallback((item, forceSkill) => {
    if (!item?.exam_id) return;
    const skill = forceSkill || item.next_skill || (sectionsEnabled[item.skill_hint] !== false ? item.skill_hint : null);
    if (!skill) return;
    const go = () => navigate(buildSkillHref(item.exam_id, skill));
    if (skill === 'writing' || skill === 'speaking') {
      setExamConfirm({ item, skill, action: go });
    } else {
      go();
    }
  }, [navigate, sectionsEnabled]);
  const openExamSkill = useCallback((item, skill) => openExam(item, skill), [openExam]);

  // Anchor refs so the "View …" links can scroll to the right section.
  // resetTo controls how the All-Exams table is set up before scrolling
  // (for example, "View full progress" wants All Skills + All status).
  const tableRef = useRef(null);
  const skillsRef = useRef(null);
  const [tableResetSignal, setTableResetSignal] = useState(0);
  const tableResetIntent = useRef({ tab: null, status: null });
  const scrollTo = (ref) => {
    if (ref?.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const goToAllExams = (intent = {}) => {
    tableResetIntent.current = intent;
    setTableResetSignal((n) => n + 1);
    scrollTo(tableRef);
  };
  const goToSkills = () => scrollTo(skillsRef);

  if (authLoading || loading) {
    return (
      <Frame isMobile={isMobile}>
        <DashboardSkeleton isMobile={isMobile} isCompact={isCompact} />
      </Frame>
    );
  }
  if (error) {
    return (
      <Frame isMobile={isMobile}>
        <div style={{ padding: 24, background: C.white, border: `1px solid ${C.border}`, borderRadius: 18, color: C.body }}>{error}</div>
      </Frame>
    );
  }

  const d = data || {};
  const continueItem = filteredItem(d.continue_learning);
  const nextUp = filteredItem(d.next_up);
  // Only show exams that have at least one task across any skill — empty
  // shells (admin created but no questions uploaded yet) stay hidden so
  // students don't see broken / blank papers.
  // The backend may now paginate `all_exams` (only the current page
  // arrives). The aggregate stats below use `skills_aggregate` /
  // `skills_*_all` fields the backend computes from the FULL summary
  // set, so the skill cards and overall % stay accurate.
  const allExams = (d.all_exams || []).map(filteredItem).filter((x) => x && x.tasks_total > 0);
  const completed = allExams.filter((x) => x.status === 'completed');
  const inProgress = allExams.filter((x) => x.status === 'in_progress');
  const notStarted = allExams.filter((x) => x.status === 'not_started');
  const examsPagination = d.all_exams_pagination || null;
  // Fall back to recomputing locally when the backend didn't send
  // aggregates (older deploy or non-paginated mode) — keeps the
  // dashboard working in either case.
  const aggregate = d.skills_aggregate;
  const perSkill = aggregate
    ? {
        writing: { total: aggregate.writing?.total || 0, completed: aggregate.writing?.completed || 0, inProgress: aggregate.writing?.in_progress || 0, notStarted: aggregate.writing?.not_started || 0 },
        reading: { total: aggregate.reading?.total || 0, completed: aggregate.reading?.completed || 0, inProgress: aggregate.reading?.in_progress || 0, notStarted: aggregate.reading?.not_started || 0 },
        speaking: { total: aggregate.speaking?.total || 0, completed: aggregate.speaking?.completed || 0, inProgress: aggregate.speaking?.in_progress || 0, notStarted: aggregate.speaking?.not_started || 0 },
        listening: { total: aggregate.listening?.total || 0, completed: aggregate.listening?.completed || 0, inProgress: aggregate.listening?.in_progress || 0, notStarted: aggregate.listening?.not_started || 0 },
      }
    : (() => {
        const local = {
          writing: { total: 0, completed: 0, inProgress: 0, notStarted: 0 },
          reading: { total: 0, completed: 0, inProgress: 0, notStarted: 0 },
          speaking: { total: 0, completed: 0, inProgress: 0, notStarted: 0 },
          listening: { total: 0, completed: 0, inProgress: 0, notStarted: 0 },
        };
        allExams.forEach((x) => {
          Object.entries(x.skills || {}).forEach(([skKey, sk]) => {
            if (sk && sk.has_content && local[skKey]) {
              local[skKey].total += 1;
              if (sk.status === 'completed') local[skKey].completed += 1;
              else if (sk.status === 'in_progress') local[skKey].inProgress += 1;
              else local[skKey].notStarted += 1;
            }
          });
        });
        return local;
      })();
  const skillsCompletedAll = d.skills_completed_all
    ?? Object.values(perSkill).reduce((sum, s) => sum + s.completed, 0);
  const skillsAvailableAll = d.skills_available_all
    ?? Object.values(perSkill).reduce((sum, s) => sum + s.total, 0);
  const overallProgress = skillsAvailableAll
    ? Math.round((skillsCompletedAll / skillsAvailableAll) * 100)
    : 0;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif", color: C.ink }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: isMobile ? '20px 16px 24px' : '28px 28px 36px' }}>

        {/* ─── 1. HEADER ──────────────────────────────────────────────── */}
        <Header
          isMobile={isMobile}
          user={d.user}
          onLogout={logout}
          onHome={() => navigate('/')}
          onProfile={() => navigate('/credit-history')}
          refreshing={refreshing}
        />

        {/* SIMPLE LAYOUT — students were getting lost in the analytics
            (top-stat cards, donut, streak tracker, achievements). Trimmed
            down to just two things they actually need: pick a skill, see
            and start an exam. Everything else is intentionally hidden;
            it can be brought back from git history when we want to
            re-introduce gamification. */}

        {/* Browse-by-skill cards — clicking one filters the table below. */}
        <SkillCardRow
          perSkill={perSkill}
          sectionsEnabled={sectionsEnabled}
          isMobile={isMobile}
          isCompact={isCompact}
          onPick={(skill) => goToAllExams({ tab: skill, status: 'all' })}
        />

        {/* Full-width exam list. */}
        <div ref={tableRef} style={{ marginTop: 14, marginBottom: 22 }}>
          <AllExamsTable
            allExams={allExams}
            inProgress={inProgress}
            completed={completed}
            notStarted={notStarted}
            onOpen={openExam}
            onOpenSkill={openExamSkill}
            isMobile={isMobile}
            sectionsEnabled={sectionsEnabled}
            resetSignal={tableResetSignal}
            resetIntent={tableResetIntent.current}
            serverPagination={examsPagination}
            serverPage={examPage}
            onServerPageChange={loadExamPage}
            serverPageLoading={examPageLoading}
            serverSkill={examSkill}
            onServerSkillChange={onSkillTabChange}
            skillsAggregate={aggregate}
          />
        </div>

        <footer style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: C.faint }}>
          © {new Date().getFullYear()} FET Prep. All rights reserved.
        </footer>
      </div>

      {examConfirm && (
        <ExamStartConfirm
          item={examConfirm.item}
          skill={examConfirm.skill}
          aiCredits={user?.ai_credits ?? 0}
          capabilities={user?.capabilities}
          userPlan={user?.plan}
          onCancel={() => setExamConfirm(null)}
          onBuyCredits={() => {
            setExamConfirm(null);
            navigate('/pricing?locked=credits&return_to=/dashboard');
          }}
          onUpgradePlan={() => {
            setExamConfirm(null);
            navigate(`/pricing?locked=${examConfirm.skill}&return_to=/dashboard`);
          }}
          onConfirm={() => {
            const action = examConfirm.action;
            setExamConfirm(null);
            if (typeof action === 'function') action();
          }}
        />
      )}
    </div>
  );
}

// "Start exam?" gate for the writing / speaking flows. Reading uses its own
// modal inside ReadingHome (gated only on the Exam-mode buttons), so this
// component never fires for reading.
//
// Writing also has a pre-flight credit check: starting a writing attempt
// costs 1 AI credit on the backend, so if the student is at zero we block
// here instead of letting them spend an hour writing only to be rejected
// at submit time.
function ExamStartConfirm({ item, skill, aiCredits = 0, capabilities, userPlan, onCancel, onConfirm, onBuyCredits, onUpgradePlan }) {
  const meta = SKILL_META[skill] || SKILL_META.writing;
  // Plan-level access check. WRITING and SPEAKING get blocked here because
  // the dashboard click drops the student straight into a paid attempt.
  // READING is exempt — the dashboard reading click goes into the FREE
  // practice flow, and the timed-exam gate lives on the Exam-mode button
  // inside ReadingHome.
  //
  // We prefer the backend's capability_map (when present), and fall back
  // to plan-based rules so the gate doesn't silently fail open on a
  // backend that hasn't shipped the `capabilities` payload yet.
  const planAllows = (() => {
    if (capabilities && typeof capabilities[skill] === 'boolean') return capabilities[skill];
    const plan = String(userPlan || 'free').toLowerCase();
    if (skill === 'writing') return ['promo', 'basic', 'ai'].includes(plan) || Number(aiCredits) > 0;
    if (skill === 'reading') return ['promo', 'basic', 'ai'].includes(plan);
    if (skill === 'speaking') return ['promo', 'ai'].includes(plan) && Number(aiCredits) > 0;
    return true;
  })();
  // Reading was previously exempt from the dashboard plan-lock (because
  // practice mode was meant to be free) — but the product rule is now
  // "reading is paid; only plan holders see the section at all", so we
  // gate it here too. Free users get the upgrade modal before being
  // dropped into onboarding / reading-home.
  const planLocked = !planAllows;
  const needsCredit = !planLocked && skill === 'writing' && Number(aiCredits) < 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'grid', placeItems: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white, borderRadius: 18,
          maxWidth: 440, width: '100%',
          padding: 24, boxShadow: '0 18px 48px rgba(15,23,42,.28)',
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{
            width: 42, height: 42, borderRadius: 12,
            background: planLocked ? C.blueSoft : needsCredit ? C.amberSoft : meta.bg,
            color: planLocked ? C.blue : needsCredit ? C.amber : meta.tone,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0,
          }} aria-hidden="true">{planLocked ? '🔒' : needsCredit ? '🪙' : meta.icon}</span>
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>
              {meta.label} exam
            </div>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.ink, lineHeight: 1.25, marginTop: 2 }}>
              {planLocked ? 'Plan upgrade required'
                : needsCredit ? 'Not enough AI credits'
                : `Start ${item.exam_title || 'this exam'}?`}
            </div>
          </div>
        </div>

        {planLocked ? (
          <div style={{ fontSize: 14, color: C.body, lineHeight: 1.6, marginBottom: 18 }}>
            {meta.label} exams aren't part of your current plan. Upgrade once and you'll unlock every {meta.label.toLowerCase()} exam on the platform.
          </div>
        ) : needsCredit ? (
          <div style={{ fontSize: 14, color: C.body, lineHeight: 1.6, marginBottom: 18 }}>
            Each writing attempt uses <strong style={{ color: C.ink }}>1 AI credit</strong> for the AI marker. You currently have <strong style={{ color: C.amberInk || '#92400E' }}>0 credits</strong>. Top up before starting so your work can be marked.
          </div>
        ) : (
          <div style={{ fontSize: 14, color: C.body, lineHeight: 1.6, marginBottom: 18 }}>
            You're about to begin the {meta.label.toLowerCase()} section. Make sure you have a quiet place and {item.time_mins ? `about ${item.time_mins} minutes` : 'enough time'} before continuing.
            {skill === 'writing' && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
                This will use <strong style={{ color: C.ink }}>1 AI credit</strong>. You have {aiCredits} {aiCredits === 1 ? 'credit' : 'credits'} available.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              height: 42, padding: '0 16px', borderRadius: 10,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.body, fontWeight: 800, fontSize: 13,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >Cancel</button>
          {planLocked ? (
            <button
              type="button"
              onClick={onUpgradePlan}
              style={{
                height: 42, padding: '0 18px', borderRadius: 10,
                border: 'none', background: C.blue, color: '#fff',
                fontWeight: 800, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '.02em',
              }}
            >🔒 View plans</button>
          ) : needsCredit ? (
            <button
              type="button"
              onClick={onBuyCredits}
              style={{
                height: 42, padding: '0 18px', borderRadius: 10,
                border: 'none', background: C.amber, color: '#fff',
                fontWeight: 800, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '.02em',
              }}
            >🪙 Buy AI credits</button>
          ) : (
            <button
              type="button"
              onClick={onConfirm}
              style={{
                height: 42, padding: '0 18px', borderRadius: 10,
                border: 'none', background: C.indigo, color: '#fff',
                fontWeight: 800, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '.02em',
              }}
            >Start now →</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Browse by skill — Skill Card Row ───────────────────────────────────

// One large clickable card per enabled skill. Click → scroll to the
// AllExamsTable with that skill tab pre-selected (so the user sees only
// that skill's exams). Skills the admin has flagged as "coming soon"
// (sectionsEnabled[sk] === false) still appear, but as a disabled card so
// the student knows it's planned.
function SkillCardRow({ perSkill, sectionsEnabled, isMobile, isCompact, onPick }) {
  const order = ['writing', 'reading', 'speaking', 'listening'];
  const visible = order.filter((sk) => {
    const stats = perSkill[sk];
    const flagged = sectionsEnabled && Object.prototype.hasOwnProperty.call(sectionsEnabled, sk);
    // Show the card when (a) there is content for this skill, OR (b) the
    // admin has explicitly toggled the skill (so a "Coming soon" placeholder
    // is intentional rather than accidental).
    return (stats && stats.total > 0) || flagged;
  });
  if (!visible.length) return null;
  return (
    <div style={{ ...cardWrap, marginBottom: 22 }}>
      <SectionHead>Browse exams by skill</SectionHead>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : isCompact ? 'repeat(3, 1fr)' : `repeat(${visible.length}, minmax(0, 1fr))`,
        gap: 12,
      }}>
        {visible.map((sk) => {
          const meta = SKILL_META[sk];
          const stats = perSkill[sk] || { total: 0, completed: 0 };
          const enabled = sectionsEnabled?.[sk] !== false;
          const hasContent = stats.total > 0;
          const clickable = enabled && hasContent;
          const pct = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;
          return (
            <button
              key={sk}
              type="button"
              onClick={clickable ? () => onPick(sk) : undefined}
              disabled={!clickable}
              style={{
                textAlign: 'left',
                padding: 14,
                background: clickable ? C.white : C.surfaceSoft,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                cursor: clickable ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                opacity: clickable ? 1 : 0.65,
                transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                minHeight: 130,
              }}
              onMouseEnter={(e) => {
                if (!clickable) return;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 6px 20px ${meta.tone}1f`;
                e.currentTarget.style.borderColor = meta.tone;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = C.border;
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: meta.bg, color: meta.tone,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }} aria-hidden="true">{meta.icon}</span>
                {!enabled ? (
                  <span style={{ fontSize: 9, padding: '3px 8px', borderRadius: 999, background: C.surfaceSoft, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 800 }}>Soon</span>
                ) : !hasContent ? (
                  <span style={{ fontSize: 9, padding: '3px 8px', borderRadius: 999, background: C.surfaceSoft, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 800 }}>No exams yet</span>
                ) : (
                  <span style={{ fontSize: 12, color: meta.tone, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                    {stats.completed}/{stats.total}
                  </span>
                )}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{meta.label}</div>
                <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginTop: 2 }}>
                  {!enabled
                    ? 'Coming soon'
                    : !hasContent
                      ? 'Admin hasn\'t added exams yet'
                      : stats.total === 1
                        ? '1 exam available'
                        : `${stats.total} exams available`}
                </div>
              </div>
              {clickable && (
                <>
                  <div style={{ flex: 1 }} />
                  <div style={{ height: 6, background: C.surfaceSoft, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: meta.tone, transition: 'width .25s ease' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: C.muted, fontWeight: 700 }}>
                    <span>{pct}% complete</span>
                    <span style={{ color: meta.tone }}>View {meta.label.toLowerCase()} →</span>
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function scoreToneColor(s) { return (s || 0) >= 70 ? C.green : (s || 0) >= 50 ? C.blue : (s || 0) > 0 ? C.amber : C.muted; }
function scoreLabel(s) {
  if (!s) return 'No scored exams yet';
  if (s >= 85) return 'Excellent!';
  if (s >= 70) return 'Good job!';
  if (s >= 55) return 'Keep going';
  return 'Needs practice';
}
function deltaColor(d) { if (d == null) return C.muted; if (d > 0) return C.green; if (d < 0) return C.amber; return C.muted; }
function deltaLabel(d) {
  if (d == null) return '';
  if (d > 0) return `+${d} vs last week`;
  if (d < 0) return `${d} vs last week`;
  return 'Same as last week';
}
function weakestSkill(breakdown, enabled) {
  if (!breakdown) return null;
  const order = ['writing', 'reading', 'speaking'];
  const candidates = order
    .filter((sk) => breakdown[sk]?.has_content && enabled?.[sk] !== false)
    .map((sk) => ({ sk, pct: breakdown[sk].pct }));
  if (!candidates.length) return null;
  candidates.sort((a, b) => a.pct - b.pct);
  return candidates[0];
}

// ── Frame + Header ────────────────────────────────────────────────────

function Frame({ isMobile, children }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", color: C.ink }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: isMobile ? '20px 16px' : '28px 28px' }}>{children}</div>
    </div>
  );
}

// Skeleton placeholder shown while the dashboard payload is loading. The
// shapes mirror the real components so users see a coherent layout
// transitioning to data instead of a centred spinner. Animated with a
// CSS @keyframes shimmer for activity feedback.
function DashboardSkeleton({ isMobile, isCompact }) {
  const shimmer = {
    background: `linear-gradient(90deg, ${C.surfaceSoft} 0%, #E8EDF2 50%, ${C.surfaceSoft} 100%)`,
    backgroundSize: '200% 100%',
    animation: 'fetSkeletonShimmer 1.4s infinite linear',
    borderRadius: 8,
  };
  const block = (h, w = '100%') => ({ ...shimmer, height: h, width: w });
  const card = { ...cardWrap, padding: 16 };
  return (
    <>
      <style>{`@keyframes fetSkeletonShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={block(28, '320px')} />
          <div style={{ ...block(14, '220px'), marginTop: 8 }} />
        </div>
        <div style={{ ...block(40, '160px'), borderRadius: 999 }} />
      </div>
      {/* 5 top stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : isCompact ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', gap: 14, marginBottom: 22 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ ...card, display: 'flex', gap: 12 }}>
            <div style={{ ...shimmer, width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={block(11, '70%')} />
              <div style={{ ...block(22, '55%'), marginTop: 8 }} />
              <div style={{ ...block(8, '100%'), marginTop: 10 }} />
            </div>
          </div>
        ))}
      </div>
      {/* 3-col main row */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isCompact ? '1fr 1fr' : 'minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 0.8fr)', gap: 14, marginBottom: 22 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} style={{ ...card, minHeight: 220 }}>
            <div style={block(14, '40%')} />
            <div style={{ ...block(60, '100%'), marginTop: 14, borderRadius: 12 }} />
            <div style={{ ...block(60, '100%'), marginTop: 10, borderRadius: 12 }} />
          </div>
        ))}
      </div>
      {/* 2-col row: All Exams + Skills */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isCompact ? '1fr' : 'minmax(0, 2.2fr) minmax(0, 1fr)', gap: 14 }}>
        <div style={{ ...card, minHeight: 380 }}>
          <div style={block(14, '30%')} />
          <div style={{ ...block(40, '100%'), marginTop: 12, borderRadius: 14 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ ...block(50, '100%'), marginTop: 10, borderRadius: 12 }} />
          ))}
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ ...card, minHeight: 220 }}>
            <div style={block(14, '50%')} />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ ...block(36, '100%'), marginTop: 10, borderRadius: 8 }} />
            ))}
          </div>
          <div style={{ ...card, minHeight: 220 }}>
            <div style={block(14, '50%')} />
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} style={{ ...block(48, '100%'), marginTop: 10, borderRadius: 10 }} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function Header({ isMobile, user, onLogout, onHome, onProfile, refreshing }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 19 : 28, fontWeight: 900, color: C.ink, letterSpacing: '-.4px', lineHeight: 1.2 }}>FET Preparation Dashboard</h1>
        <div style={{ fontSize: isMobile ? 12 : 14, color: C.muted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Practice exams and track your progress</span>
          {refreshing && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 700,
              padding: '3px 9px', borderRadius: 999,
              background: C.indigoSoft, color: C.indigo,
              letterSpacing: '.04em',
            }}>
              <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: C.indigo, animation: 'fetPulse 1.2s infinite ease-in-out' }} />
              Refreshing
            </span>
          )}
        </div>
        <style>{`@keyframes fetPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={onHome} title="Back to home" style={iconBtn}>⌂</button>
        <div ref={ref} style={{ position: 'relative' }}>
          <button type="button" onClick={() => setOpen((v) => !v)} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: C.white, border: `1px solid ${C.border}`, borderRadius: 999,
            padding: '6px 12px 6px 6px', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <span style={{
              width: 32, height: 32, borderRadius: '50%',
              background: C.indigo, color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 12,
            }}>{user?.initials || '··'}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: C.ink, maxWidth: isMobile ? 80 : 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || 'Student'}</span>
            <span style={{ color: C.muted, fontSize: 10 }}>▾</span>
          </button>
          {open && (
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
              minWidth: 200, background: C.white, border: `1px solid ${C.border}`,
              borderRadius: 12, boxShadow: '0 10px 28px rgba(15,23,42,.12)',
              padding: 6, zIndex: 50,
            }}>
              <DropItem onClick={() => { setOpen(false); onProfile(); }} icon="👤">My Profile</DropItem>
              <DropItem onClick={() => { setOpen(false); onHome(); }} icon="🏠">Home</DropItem>
              <DropItem onClick={() => { setOpen(false); onLogout && onLogout(); }} icon="↩" danger>Sign out</DropItem>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const iconBtn = { width: 40, height: 40, borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, color: C.body, cursor: 'pointer', fontFamily: 'inherit', fontSize: 16 };

function DropItem({ icon, children, onClick, danger }) {
  return (
    <button type="button" onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
      background: 'transparent', border: 'none', padding: '10px 12px',
      borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 13, fontWeight: 700, color: danger ? C.red : C.ink, textAlign: 'left',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = C.surfaceSoft; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >{icon} {children}</button>
  );
}

// ── Stat Cards ─────────────────────────────────────────────────────────

function StatCard({ icon, iconBg, iconColor, label, value, footer }) {
  return (
    <div className="fet-stat-card" style={{
      background: C.white, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: 14,
      display: 'flex', alignItems: 'flex-start', gap: 12,
      boxShadow: '0 1px 2px rgba(15,23,42,.04)',
      minWidth: 0,
    }}>
      <span style={{
        width: 36, height: 36, borderRadius: 10,
        background: iconBg, color: iconColor,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, flexShrink: 0,
      }}>{icon}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '.02em', lineHeight: 1.25 }}>{label}</div>
        <div className="fet-stat-value" style={{
          fontSize: 22, color: C.ink, fontWeight: 900, marginTop: 2,
          lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.4px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{value}</div>
        <div style={{ marginTop: 6 }}>{footer}</div>
      </div>
    </div>
  );
}

function ProgressBar({ pct, color, suffix }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: C.surfaceSoft, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width .3s ease' }} />
      </div>
      {suffix && <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{suffix}</span>}
    </div>
  );
}

// ── Continue + Recommended ─────────────────────────────────────────────

function ContinueCard({ items, onOpen, onOpenSkill, isMobile }) {
  const [showAll, setShowAll] = useState(false);
  const list = items || [];
  // Sort by last_activity DESC so the freshest in-progress item is on top.
  const sorted = useMemo(() => [...list].sort((a, b) => {
    const aT = a.last_activity ? new Date(a.last_activity).getTime() : 0;
    const bT = b.last_activity ? new Date(b.last_activity).getTime() : 0;
    return bT - aT;
  }), [list]);
  const initial = 3;
  const visible = showAll ? sorted : sorted.slice(0, initial);
  return (
    <div style={cardWrap}>
      <SectionHead
        icon="▶"
        iconColor={C.indigo}
        action={list.length > 0 ? (
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>
            {list.length} pending
          </span>
        ) : null}
      >
        Continue Your Exam
      </SectionHead>
      {sorted.length === 0 ? (
        <EmptyState text="Nothing in progress — start an exam to see it here." />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {visible.map((item) => (
            <PendingExamCard
              key={item._rowKey || item.exam_id}
              item={item}
              onOpen={() => onOpen(item)}
              isMobile={isMobile}
            />
          ))}
          {sorted.length > initial && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              style={{
                background: 'transparent', border: `1px solid ${C.border}`,
                padding: '8px', borderRadius: 10,
                fontSize: 12, fontWeight: 800, color: C.body,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {showAll ? 'Show less ↑' : `Show all ${sorted.length} pending ↓`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PendingExamCard({ item, onOpen, isMobile }) {
  // Use next_skill — the skill the Continue button will jump into — for the
  // headline progress bar. Per-skill mini-bars below show the full picture.
  const skill = item.next_skill || item.skill_hint || 'writing';
  const meta = SKILL_META[skill] || SKILL_META.writing;
  const sub = item.skills?.[skill] || { done: 0, total: 0, pct: 0 };

  // All skills the exam has content for AND that are admin-enabled. Each
  // gets its own mini progress bar so the user sees exactly where they
  // stand in every section, not just the next-skill one.
  const skillsToShow = ['writing', 'reading', 'speaking'].filter((sk) => item.skills?.[sk]?.has_content);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: 10,
      padding: 12,
      background: C.surfaceSoft,
      borderRadius: 12,
    }}>
      <span style={{
        width: 40, height: 40, borderRadius: 10,
        background: meta.bg, color: meta.tone,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, flexShrink: 0,
      }}>{meta.icon}</span>
      <div style={{ minWidth: 0, display: 'grid', gap: 8 }}>
        {/* Title + meta + Continue button */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.ink, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.exam_title}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
              {item.time_mins ? <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{item.time_mins} min</span> : null}
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
                {item.last_activity ? `Last: ${fmtDate(item.last_activity)}` : item.draft_saved_at ? `Draft: ${fmtDate(item.draft_saved_at)}` : 'Not yet attempted'}
              </span>
              {item.has_draft && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                  background: C.indigoSoft, color: C.indigo,
                  letterSpacing: '.04em', textTransform: 'uppercase',
                }}>
                  💾 Draft
                </span>
              )}
              <span style={{ fontSize: 11, color: meta.tone, fontWeight: 800, marginLeft: 'auto' }}>
                {sub.status === 'in_progress' ? `Resume ${meta.label}` : sub.status === 'completed' ? 'Done' : `Start ${meta.label}`}
              </span>
            </div>
          </div>
          <button type="button" onClick={onOpen} style={{ ...primaryCta, padding: '6px 12px', fontSize: 11 }}>
            Continue →
          </button>
        </div>

        {/* Per-skill mini progress bars */}
        <div style={{ display: 'grid', gap: 5, marginTop: 2 }}>
          {skillsToShow.map((sk) => {
            const m = SKILL_META[sk];
            const s = item.skills[sk];
            const isNext = sk === skill;
            const done = s.status === 'completed';
            // Compute a visual-only "draft pct" boost: if writing has a draft
            // (typed but unsubmitted) and the user has 0 submitted parts yet,
            // show ~25% so the bar is visibly non-zero and the user knows the
            // draft is registered even though no part is fully done.
            const draftBoost = sk === 'writing' && s.has_draft && s.done === 0 ? 25 : 0;
            const visualPct = Math.max(s.pct, draftBoost);
            return (
              <div key={sk} style={{
                display: 'grid', gridTemplateColumns: '20px 60px 1fr 50px',
                alignItems: 'center', gap: 8,
              }}>
                <span style={{
                  width: 20, height: 20, borderRadius: 6,
                  background: m.bg, color: m.tone,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, opacity: isNext ? 1 : 0.85,
                }}>{m.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: isNext ? C.ink : C.body, letterSpacing: '.02em' }}>
                  {m.label}
                  {done && <span style={{ marginLeft: 4, color: C.green }} aria-hidden="true">✓</span>}
                  {!done && draftBoost > 0 && <span style={{ marginLeft: 4, fontSize: 9, color: m.tone }} aria-hidden="true" title="Draft saved">💾</span>}
                </span>
                <div style={{
                  height: 6, background: C.white, borderRadius: 3, overflow: 'hidden',
                  border: `1px solid ${C.border}`,
                }}>
                  <div style={{
                    width: `${visualPct}%`, height: '100%',
                    background: draftBoost > 0 && s.done === 0 ? `repeating-linear-gradient(45deg, ${m.tone}, ${m.tone} 4px, ${m.tone}cc 4px, ${m.tone}cc 8px)` : m.tone,
                    transition: 'width .25s ease',
                  }} />
                </div>
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {draftBoost > 0 && s.done === 0 ? 'draft' : `${s.done}/${s.total}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RecommendedCard({ item, onOpen, onViewAll, isMobile }) {
  return (
    <div style={cardWrap}>
      <SectionHead icon="★" iconColor={C.indigo} action={<LinkBtn onClick={onViewAll}>View all exams →</LinkBtn>}>
        Recommended for You
      </SectionHead>
      {item ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12, background: C.surfaceSoft, borderRadius: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 44, height: 44, borderRadius: 10, background: C.indigoSoft, color: C.indigo, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>✎</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, lineHeight: 1.3 }}>{item.exam_title}</div>
              {item.subtitle && (
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.subtitle}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <Pill bg={C.indigoSoft} fg={C.indigo}>{SKILL_META[item.skill_hint]?.label || 'Mixed'}</Pill>
                <span style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>{item.time_mins ? `${item.time_mins} min` : 'No time set'}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onOpen} style={primaryCta}>
              Start Exam <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      ) : (
        <EmptyState text="Great work — you've started everything available." />
      )}
    </div>
  );
}

// ── Progress Donut ─────────────────────────────────────────────────────

function ProgressDonut({ completed, inProgress, notStarted, overallPct, isMobile, onViewAll }) {
  const total = completed + inProgress + notStarted || 1;
  const cPct = (completed / total) * 100;
  const ipPct = (inProgress / total) * 100;
  const stops = [];
  let acc = 0;
  if (cPct > 0) { stops.push(`${C.green} ${acc}deg ${acc + (cPct * 3.6)}deg`); acc += cPct * 3.6; }
  if (ipPct > 0) { stops.push(`${C.indigo} ${acc}deg ${acc + (ipPct * 3.6)}deg`); acc += ipPct * 3.6; }
  if (acc < 360) stops.push(`${C.surfaceSoft} ${acc}deg 360deg`);
  const ringStyle = stops.length ? { background: `conic-gradient(${stops.join(', ')})` } : { background: C.surfaceSoft };
  return (
    <div style={cardWrap}>
      <SectionHead action={<LinkBtn onClick={onViewAll}>View full progress →</LinkBtn>}>Your Progress</SectionHead>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 4px' }}>
        <div style={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
          <div style={{ ...ringStyle, width: '100%', height: '100%', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', inset: 14, background: C.white, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: C.ink, lineHeight: 1 }}>{overallPct}%</div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginTop: 2 }}>Overall Progress</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
          <DonutLegend dot={C.green} label={`${completed} Completed`} />
          <DonutLegend dot={C.indigo} label={`${inProgress} In Progress`} />
          <DonutLegend dot={C.faint} label={`${notStarted} Not Started`} />
        </div>
      </div>
    </div>
  );
}

function DonutLegend({ dot, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: C.body, fontWeight: 700 }}>{label}</span>
    </div>
  );
}

// ── All Exams Table ────────────────────────────────────────────────────

// Skill-segmented exam list. Primary axis is the SKILL tab (Writing /
// Reading / Speaking) so the user sees only one skill at a time and can
// commit to which one to practise. Status (Not Started / In Progress /
// Completed) is a secondary chip filter inside that view.
function AllExamsTable({ allExams, inProgress, completed, notStarted, onOpen, onOpenSkill, isMobile, sectionsEnabled, resetSignal, resetIntent, serverPagination, serverPage, onServerPageChange, serverPageLoading, serverSkill, onServerSkillChange, skillsAggregate }) {
  // Build skill tabs. With server-side pagination, the visible
  // `allExams` is only the current page (≤5 rows), so deriving tab
  // counts from it would hide tabs whose exams happen not to be on
  // the current page. We use `skillsAggregate` from the backend
  // (computed from the full library) when available so all tabs
  // always show their true library-wide counts.
  const skillTabs = useMemo(() => {
    const order = ['writing', 'reading', 'speaking'];
    const skillCounts = {};
    if (skillsAggregate) {
      order.forEach((sk) => {
        if (sectionsEnabled?.[sk] === false) { skillCounts[sk] = 0; return; }
        skillCounts[sk] = skillsAggregate[sk]?.total || 0;
      });
    } else {
      order.forEach((sk) => {
        if (sectionsEnabled?.[sk] === false) { skillCounts[sk] = 0; return; }
        skillCounts[sk] = allExams.filter((x) => x.skills?.[sk]?.has_content).length;
      });
    }
    const allCount = order.reduce((n, sk) => n + (skillCounts[sk] || 0), 0);
    const tabs = [{ key: 'all', label: 'All Skills', count: allCount }];
    order.forEach((sk) => {
      if (sectionsEnabled?.[sk] === false) return;
      if (skillCounts[sk] === 0) return;
      tabs.push({ key: sk, label: SKILL_META[sk]?.label || sk, count: skillCounts[sk] });
    });
    return tabs;
  }, [allExams, sectionsEnabled, skillsAggregate]);

  // When the parent owns the skill (server-side pagination on), mirror
  // its value here so the active-tab styling and any other derived
  // state stay in sync. Tab clicks delegate back to the parent
  // (`onServerSkillChange`) which fetches a fresh page for the new
  // skill — the parent is the source of truth in that mode.
  const usingServerSkill = !!serverPagination && typeof onServerSkillChange === 'function';
  const [localTab, setLocalTab] = useState(skillTabs[1]?.key || 'all');
  const tab = usingServerSkill ? (serverSkill || 'all') : localTab;
  const setTab = usingServerSkill ? onServerSkillChange : setLocalTab;
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const EXAM_PAGE_SIZE = 5;
  const [examPage, setExamPage] = useState(1);
  const [search, setSearch] = useState('');

  // Reset client-side page only when something the client owns changes.
  // In server mode the parent resets its own page on tab change.
  useEffect(() => { setExamPage(1); }, [statusFilter, sort, search]);

  // Honour reset signal from parent ("View all exams" / "View full progress" /
  // "View skills") — set tab + status filters as the click intended. We
  // deliberately key only on resetSignal: each click bumps it, and the
  // accompanying intent is read inline (a ref-style pattern via prop).
  useEffect(() => {
    if (!resetSignal) return;
    if (resetIntent?.tab) setTab(resetIntent.tab);
    if (resetIntent?.status) setStatusFilter(resetIntent.status);
    setSearch('');
  }, [resetSignal, resetIntent, setTab]);

  const list = useMemo(() => {
    // Each row represents an (exam × skill) pair so totals add up consistently
    // across tabs. An exam with Writing + Reading content appears as 2 rows in
    // the All-Skills tab, 1 row each in Writing and Reading tabs.
    const order = ['writing', 'reading', 'speaking'];
    let arr = [];
    allExams.forEach((x) => {
      order.forEach((sk) => {
        if (tab !== 'all' && sk !== tab) return;
        // Skip skills the admin has globally disabled.
        if (sectionsEnabled?.[sk] === false) return;
        const sub = x.skills?.[sk];
        // Only render rows where this skill actually has content. Empty
        // shells stay hidden — they belong in the admin panel, not on the
        // student dashboard.
        if (!sub || !sub.has_content) return;
        arr.push({
          ...x,
          _rowKey: `${x.exam_id}:${sk}`,
          __activeSkill: sk,
          __status: sub.status,
          __pct: sub.pct,
          __done: sub.done,
          __total: sub.total,
          __hasDraft: !!sub.has_draft,
          __draftSavedAt: sub.draft_saved_at || null,
        });
      });
    });
    if (statusFilter !== 'all') arr = arr.filter((x) => x.__status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((x) => (x.exam_title || '').toLowerCase().includes(q) || (x.subtitle || '').toLowerCase().includes(q));
    }
    if (sort === 'name') arr = [...arr].sort((a, b) => (a.exam_title || '').localeCompare(b.exam_title || '', undefined, { numeric: true, sensitivity: 'base' }));
    else if (sort === 'progress') arr = [...arr].sort((a, b) => b.__pct - a.__pct);
    else {
      // Default 'recent' sort — backend already orders by -created_at, but
      // when an admin renames an exam to "FET Writing 2" after "10" is
      // created, that bunches them confusingly. Fall through to a natural
      // title sort as a tiebreaker so "FET Writing 1" lands above
      // "FET Writing 10" instead of mixing in created order.
      arr = [...arr].sort((a, b) => (a.exam_title || '').localeCompare(b.exam_title || '', undefined, { numeric: true, sensitivity: 'base' }));
    }
    return arr;
  }, [allExams, tab, statusFilter, sort, search, sectionsEnabled]);

  // When the parent passes server-pagination metadata we render the
  // backend's page directly (no client slicing) — the `list` array
  // here is already the current page. Otherwise we fall back to
  // client-side slicing (used by older deploys / static views).
  const usingServerPagination = !!serverPagination && typeof onServerPageChange === 'function';
  const totalExamPages = usingServerPagination
    ? Math.max(1, serverPagination.total_pages || 1)
    : Math.max(1, Math.ceil(list.length / EXAM_PAGE_SIZE));
  const visible = usingServerPagination
    ? list
    : list.slice((examPage - 1) * EXAM_PAGE_SIZE, examPage * EXAM_PAGE_SIZE);
  const activePage = usingServerPagination ? (serverPage || 1) : examPage;
  const handlePageChange = usingServerPagination ? onServerPageChange : setExamPage;
  const isPageLoading = usingServerPagination && serverPageLoading;

  const statusChips = [
    { key: 'all', label: 'All' },
    { key: 'not_started', label: 'Not Started' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
  ];

  return (
    <div style={cardWrap}>
      <div style={{ marginBottom: 8, fontSize: 12, color: C.muted, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>
        Filter by skill — click to switch
      </div>
      {/* Skill tabs — pill segmented control. Each tab uses its skill colour
          even when inactive, so it never looks "disabled". */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: 4, background: C.surfaceSoft, borderRadius: 14 }}>
          {skillTabs.map((t) => {
            const active = tab === t.key;
            const meta = SKILL_META[t.key];
            const tone = meta?.tone || C.indigo;
            const bg = meta?.bg || C.indigoSoft;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = bg; e.currentTarget.style.color = tone; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.body; } }}
                style={{
                  background: active ? tone : C.white,
                  border: `1px solid ${active ? tone : C.border}`,
                  padding: '8px 16px',
                  borderRadius: 10,
                  fontSize: 13, fontWeight: 800,
                  color: active ? '#fff' : C.body,
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  boxShadow: active ? `0 4px 12px ${tone}33` : 'none',
                  transition: 'background .15s ease, color .15s ease, box-shadow .15s ease',
                }}
                aria-pressed={active}
              >
                {meta?.icon && <span aria-hidden="true" style={{ fontSize: 14 }}>{meta.icon}</span>}
                <span>{t.label}</span>
                <span style={{
                  fontSize: 10, fontWeight: 800,
                  padding: '2px 8px', borderRadius: 999,
                  background: active ? 'rgba(255,255,255,.22)' : bg,
                  color: active ? '#fff' : tone,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '.04em',
                }}>{t.count}</span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search exams…"
            style={{
              padding: '7px 12px', borderRadius: 8,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.ink, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              minWidth: 0, width: 160,
            }}
          />
          <Select value={sort} onChange={setSort} options={[
            { value: 'recent', label: 'Recent' },
            { value: 'name', label: 'Name' },
            { value: 'progress', label: 'Progress' },
          ]} />
        </div>
      </div>

      {/* Status sub-filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginRight: 4 }}>Status:</span>
        {statusChips.map((c) => {
          const active = statusFilter === c.key;
          // Tone the chip with a status-relevant colour so the filter feels
          // meaningful rather than a generic toggle.
          const toneFor = c.key === 'completed' ? C.green
            : c.key === 'in_progress' ? C.amber
            : c.key === 'not_started' ? C.muted
            : C.indigo;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setStatusFilter(c.key)}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.surfaceSoft; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = C.white; }}
              style={{
                padding: '5px 12px', borderRadius: 999,
                background: active ? toneFor : C.white,
                color: active ? '#fff' : C.body,
                border: `1px solid ${active ? toneFor : C.border}`,
                fontSize: 11, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '.02em',
                transition: 'background .15s ease, color .15s ease',
              }}
              aria-pressed={active}
            >{c.label}</button>
          );
        })}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {usingServerPagination
            ? `${serverPagination.count} ${serverPagination.count === 1 ? 'exam' : 'exams'}`
            : `${list.length} ${list.length === 1 ? 'exam-skill' : 'exam-skills'}`}
        </span>
      </div>

      {/* Rows — paginated to EXAM_PAGE_SIZE per page so the dashboard
          renders quickly regardless of how many exams the admin has
          uploaded. With 300+ exams, the previous scroll-cap approach
          still mounted all the DOM rows at once. Prev/Next + numbered
          buttons let the student step through the library. */}
      {visible.length === 0 ? (
        <EmptyState text="No exams match this filter." />
      ) : (
        <div style={{ display: 'grid', gap: 8, opacity: isPageLoading ? 0.55 : 1, transition: 'opacity .15s ease' }}>
          {visible.map((row) => (
            <ExamRow
              key={row._rowKey || row.exam_id}
              item={row}
              onOpen={onOpen}
              onOpenSkill={onOpenSkill}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      {totalExamPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 6, flexWrap: 'wrap', marginTop: 14,
        }}>
          <button
            type="button"
            onClick={() => handlePageChange(Math.max(1, activePage - 1))}
            disabled={activePage === 1 || isPageLoading}
            style={{
              padding: '7px 14px', borderRadius: 999,
              border: `1px solid ${C.border}`, background: C.white, color: C.body,
              fontSize: 12, fontWeight: 800,
              cursor: activePage === 1 ? 'default' : 'pointer',
              opacity: activePage === 1 ? 0.5 : 1, fontFamily: 'inherit',
            }}
          >← Prev</button>
          {Array.from({ length: totalExamPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              type="button"
              onClick={() => handlePageChange(page)}
              disabled={isPageLoading}
              style={{
                padding: '6px 11px', borderRadius: 999, minWidth: 32,
                border: `1px solid ${page === activePage ? C.indigo : C.border}`,
                background: page === activePage ? C.indigo : C.white,
                color: page === activePage ? '#fff' : C.body,
                fontSize: 12, fontWeight: 800, cursor: 'pointer',
                fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit',
              }}
            >{page}</button>
          ))}
          <button
            type="button"
            onClick={() => handlePageChange(Math.min(totalExamPages, activePage + 1))}
            disabled={activePage === totalExamPages || isPageLoading}
            style={{
              padding: '7px 14px', borderRadius: 999,
              border: `1px solid ${C.border}`, background: C.white, color: C.body,
              fontSize: 12, fontWeight: 800,
              cursor: activePage === totalExamPages ? 'default' : 'pointer',
              opacity: activePage === totalExamPages ? 0.5 : 1, fontFamily: 'inherit',
            }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

// Wrapped in React.memo below so parent re-renders skip rows whose `item`
// (and the stable handler refs) haven't changed. The pre-baked
// per-skill fields all live on `item.__*`, set when AllExamsTable
// builds the visible list — reading them off the item rather than
// receiving them as separate props is what lets memoization actually
// take effect (one prop instead of eight).
function ExamRowImpl({ item, onOpen, onOpenSkill, isMobile }) {
  const handleOpen = useCallback(() => {
    // Each row is bound to a specific skill — always jump to that
    // skill's flow (no ambiguous multi-skill resume).
    if (item.__activeSkill) onOpenSkill(item, item.__activeSkill);
    else onOpen(item);
  }, [item, onOpen, onOpenSkill]);
  const effStatus = item.__status || item.status;
  const skill = item.__activeSkill || item.skill_hint;
  const effPct = item.__pct == null ? item.pct : item.__pct;
  const effDone = item.__done == null ? item.tasks_done : item.__done;
  const effTotal = item.__total == null ? item.tasks_total : item.__total;
  const hasDraft = item.__hasDraft;
  const draftSavedAt = item.__draftSavedAt;
  const isCompleted = effStatus === 'completed';
  const isInProgress = effStatus === 'in_progress';
  // Draft-only state: typed locally but no part submitted yet. We treat it
  // visually as "in progress" (striped bar, "Draft" label) so the row
  // doesn't lie by saying "0/2 0%" when the user has unfinished text.
  const isDraftOnly = hasDraft && effDone === 0 && !isCompleted;
  const ctaLabel = isCompleted ? 'Retake' : (isInProgress || isDraftOnly) ? 'Continue' : 'Start';
  const ctaSolid = !isCompleted && !isInProgress && !isDraftOnly;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr auto' : 'minmax(0, 2fr) auto auto minmax(160px, 1fr) auto',
      alignItems: 'center', gap: 12,
      padding: '10px 12px', borderRadius: 12,
      background: C.white, border: `1px solid ${C.border}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.exam_title}</div>
        {isMobile && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <Pill bg={SKILL_META[skill]?.bg || C.indigoSoft} fg={SKILL_META[skill]?.tone || C.indigo}>{SKILL_META[skill]?.label || 'Mixed'}</Pill>
            {item.time_mins ? <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{item.time_mins} min</span> : null}
          </div>
        )}
      </div>
      {!isMobile && (
        <Pill bg={SKILL_META[skill]?.bg || C.indigoSoft} fg={SKILL_META[skill]?.tone || C.indigo}>
          {SKILL_META[skill]?.label || 'Mixed'}
        </Pill>
      )}
      {!isMobile && (
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>
          {item.time_mins ? `${item.time_mins} min` : '—'}
        </span>
      )}
      {!isMobile && (
        <div style={{ minWidth: 0 }}>
          {isInProgress && !isDraftOnly && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: C.amberInk, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{effDone}/{effTotal}</span>
              <ProgressBar pct={effPct} color={C.amber} suffix={`${effPct}%`} />
            </div>
          )}
          {isDraftOnly && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title={draftSavedAt ? `Draft saved ${new Date(draftSavedAt).toLocaleString()}` : 'Draft saved on this device'}>
              <span style={{
                fontSize: 11, color: C.indigo, fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>💾 Draft</span>
              <div style={{ flex: 1, height: 6, background: C.surfaceSoft, borderRadius: 3, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                <div style={{
                  width: '25%', height: '100%',
                  background: `repeating-linear-gradient(45deg, ${C.indigo}, ${C.indigo} 5px, ${C.indigo}99 5px, ${C.indigo}99 10px)`,
                  transition: 'width .25s ease',
                }} />
              </div>
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>—</span>
            </div>
          )}
          {isCompleted && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: C.greenInk, fontWeight: 800 }}>Completed</span>
              <ProgressBar pct={100} color={C.green} suffix="100%" />
            </div>
          )}
          {effStatus === 'not_started' && !isDraftOnly && (
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>Not Started</span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button type="button" onClick={handleOpen} style={ctaSolid ? primaryCta : outlineCta}>{ctaLabel}</button>
      </div>
    </div>
  );
}

const ExamRow = React.memo(ExamRowImpl);

// ── Skill Breakdown ────────────────────────────────────────────────────

function SkillBreakdown({ skills, sectionsEnabled, onViewAll }) {
  // Only render skills the platform actually has — i.e. ones that admin
  // can toggle on/off, OR ones that have submitted content. "Listening"
  // doesn't exist as a section yet (no admin toggle, no exam content), so
  // it's hidden until that changes.
  const visibleSkills = ['writing', 'reading', 'listening', 'speaking'].filter((sk) => {
    if (sectionsEnabled && Object.prototype.hasOwnProperty.call(sectionsEnabled, sk)) return true;
    if (skills?.[sk]?.has_content) return true;
    return false;
  });
  return (
    <div style={cardWrap}>
      <SectionHead action={<LinkBtn onClick={onViewAll}>View skills →</LinkBtn>}>Skill Breakdown</SectionHead>
      <div style={{ display: 'grid', gap: 16 }}>
        {visibleSkills.map((sk) => {
          const meta = SKILL_META[sk];
          const s = skills[sk] || { pct: 0, has_content: false };
          const enabled = sectionsEnabled?.[sk] !== false;
          // Clamp pct to [0, 100]. The backend's reading skill can return
          // > 100% when the count of "parts done" exceeds the count of
          // "parts with content" (a known bug while the legacy
          // question_count property still treats new content shapes as 0).
          // The fix is to deploy the new backend; until then, clamp here so
          // students don't see "500%".
          const safePct = Math.max(0, Math.min(100, Number(s.pct) || 0));
          return (
            <div key={sk} style={{ display: 'grid', gridTemplateColumns: '38px 1fr auto', alignItems: 'center', columnGap: 12, rowGap: 6 }}>
              {/* Icon tile */}
              <span style={{
                width: 38, height: 38, borderRadius: 10,
                background: meta.bg, color: meta.tone,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, gridRow: 'span 2',
              }}>{meta.icon}</span>

              {/* Label row */}
              <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 6 }}>
                {meta.label}
                {!enabled && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 999, background: C.surfaceSoft, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 800 }}>Soon</span>}
              </div>
              <span style={{ fontSize: 14, color: C.ink, fontWeight: 900, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{safePct}%</span>

              {/* Progress bar (spans the right two columns under the label/%) */}
              <div style={{
                gridColumn: '2 / 4',
                height: 8, background: C.surfaceSoft, borderRadius: 4, overflow: 'hidden',
              }}>
                <div style={{
                  width: `${safePct}%`,
                  height: '100%',
                  background: meta.tone,
                  borderRadius: 4,
                  transition: 'width .35s ease',
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Achievements ───────────────────────────────────────────────────────

function Achievements({ achievements }) {
  // Show count of unlocked achievements as the section action — no extra
  // navigation since this list already shows everything the user has.
  const unlocked = (achievements || []).filter((a) => a.completed).length;
  const total = (achievements || []).length;
  return (
    <div style={cardWrap}>
      <SectionHead action={
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {unlocked} / {total} unlocked
        </span>
      }>Achievements</SectionHead>
      <div style={{ display: 'grid', gap: 10 }}>
        {achievements.map((a) => (
          <div key={a.key} style={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'center', gap: 12, padding: '10px 12px', background: C.surfaceSoft, borderRadius: 10 }}>
            <span style={{
              width: 40, height: 40, borderRadius: '50%',
              background: a.completed ? C.amberSoft : C.surfaceSoft,
              color: a.completed ? C.amberInk : C.faint,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, border: `1px solid ${a.completed ? 'rgba(245,158,11,.3)' : C.border}`,
            }}>{a.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: a.completed ? C.ink : C.muted }}>{a.title}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{a.description}</div>
            </div>
            <span style={{
              width: 24, height: 24, borderRadius: '50%',
              background: a.completed ? C.green : C.surfaceSoft,
              color: a.completed ? '#fff' : C.faint,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800,
            }}>{a.completed ? '✓' : '○'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bottom Strip ───────────────────────────────────────────────────────

function BottomStrip({ weeklyActivity, streakDays, weakestSkill, weeklyTarget, isMobile }) {
  const tip = !weakestSkill
    ? { title: 'Pick up the pace', body: 'Aim for one exam a day' }
    : streakDays >= 5
      ? { title: "You're on fire!", body: `${streakDays}-day streak — keep it going` }
      : { title: 'Pick up the pace', body: 'Aim for one exam a day' };
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(260px, 1fr) repeat(3, minmax(0, 1fr))',
      gap: 14, marginBottom: 6,
    }}>
      <div style={cardWrap}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>Keep your streak going!</span>
          <span aria-hidden="true">🔥</span>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>You're on a {streakDays}-day streak</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
          {(weeklyActivity.length ? weeklyActivity : ['M','T','W','T','F','S','S'].map((l) => ({ label: l, active: false }))).map((day, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: '.06em' }}>{day.label}</span>
              <span style={{
                width: 20, height: 20, borderRadius: '50%',
                background: day.active ? C.green : C.surfaceSoft,
                color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
              }}>{day.active ? '✓' : ''}</span>
            </div>
          ))}
        </div>
      </div>

      <MotivationCard icon="🎯" iconBg={C.indigoSoft} iconColor={C.indigo}
        title="Focus Skill"
        bold={weakestSkill ? SKILL_META[weakestSkill.sk]?.label : '—'}
        body={weakestSkill ? `${weakestSkill.pct}% — try to improve your weakest skill` : 'Complete a few exams to see weak areas'}
      />
      <MotivationCard icon="📅" iconBg={C.amberSoft} iconColor={C.amber}
        title="Study Plan"
        bold={`${weeklyTarget || 0} exam${weeklyTarget === 1 ? '' : 's'} this week`}
        body="Stay consistent and track progress"
      />
      <MotivationCard icon="🚀" iconBg={C.indigoSoft} iconColor={C.indigo}
        title={tip.title}
        bold=""
        body={tip.body}
      />
    </div>
  );
}

function MotivationCard({ icon, iconBg, iconColor, title, bold, body }) {
  return (
    <div style={cardWrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 44, height: 44, borderRadius: 10, background: iconBg, color: iconColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{title}</div>
          {bold && <div style={{ fontSize: 13, color: C.body, marginTop: 2, fontWeight: 700 }}>{bold}</div>}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{body}</div>
        </div>
      </div>
    </div>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────

const cardWrap = {
  background: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: 16,
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

function SectionHead({ icon, iconColor, action, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && (
          <span style={{ color: iconColor || C.indigo, fontSize: 14, fontWeight: 800 }}>{icon}</span>
        )}
        <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{children}</span>
      </div>
      {action}
    </div>
  );
}

function LinkBtn({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      background: 'transparent', border: 'none', padding: 0,
      fontSize: 12, fontWeight: 800, color: C.indigo,
      cursor: 'pointer', fontFamily: 'inherit',
    }}>{children}</button>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ padding: 18, background: C.surfaceSoft, borderRadius: 10, color: C.muted, fontSize: 13, textAlign: 'center' }}>{text}</div>
  );
}

function Pill({ bg, fg, children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 999,
      background: bg, color: fg,
      fontSize: 11, fontWeight: 800, letterSpacing: '.02em',
    }}>{children}</span>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{
      padding: '7px 10px', borderRadius: 8,
      border: `1px solid ${C.border}`, background: C.white,
      color: C.body, fontSize: 12, fontWeight: 700,
      cursor: 'pointer', fontFamily: 'inherit',
    }}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

const primaryCta = {
  background: C.indigo, color: '#fff', border: 'none',
  padding: '8px 16px', borderRadius: 10,
  fontSize: 12, fontWeight: 800, cursor: 'pointer',
  fontFamily: 'inherit', letterSpacing: '.02em',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
const outlineCta = {
  background: C.white, color: C.body, border: `1px solid ${C.border}`,
  padding: '8px 16px', borderRadius: 10,
  fontSize: 12, fontWeight: 800, cursor: 'pointer',
  fontFamily: 'inherit', letterSpacing: '.02em',
};
